import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Edit, Delete, Search } from '@mui/icons-material';
import { getRequest, postRequest, putRequest, deleteRequest, server, patchRequest } from '../config';
import { toast } from 'react-toastify';

const ManageCities = () => {
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityName, setCityName] = useState('');
  const [pincodes, setPincodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    const res = await getRequest(`${server.uri}cities`);
    if (res) {
      setCities(res);
      setAllCities(res);
    }
  };

  const handleAddCity = () => {
    setCityName('');
    setPincodes([]);
    setEditingCity(null);
    setOpenDialog(true);
  };

  const handleEditCity = (city) => {
    setEditingCity(city);
    setCityName(city.name);
    setPincodes(city.pincodes || []);
    setOpenDialog(true);
  };

  const handleDeleteCity = async (cityId) => {
    if (window.confirm('Are you sure you want to delete this city?')) {
      await deleteRequest(`${server.uri}cities/${cityId}`);
      fetchCities();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveCity = async () => {
    const payload = {
      name: cityName,
      pincodes,
      identifier:cityName
    };

    if (editingCity) {
      await patchRequest(`${server.uri}cities/${editingCity.cityId}`, payload);
      toast.success('City Updated Successfully')
    } else {
      await postRequest(`${server.uri}cities`, payload);
      toast.success('City Added Successfully')
    }

    fetchCities();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allCities.filter(city =>
      city.name.toLowerCase().includes(value)
    );
    setCities(filtered);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Manage Cities
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search city by name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: 'gray', marginRight: 1 }} />,
          }}
          sx={{ width: '60%' }}
        />
        <Button variant="contained" onClick={handleAddCity}>
          Add City
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f4f6f8' }}>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Pincodes</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cities.map((city) => (
                <TableRow key={city.cityId}>
                  <TableCell>{city.name}</TableCell>
                  <TableCell>{(city.pincodes || []).join(', ')}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditCity(city)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeleteCity(city.cityId)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {cities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No cities found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCity ? 'Edit City' : 'Add New City'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="City Name"
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={pincodes}
            onChange={(e, newValue) => {
              const validPincodes = newValue
                .map((p) => p.trim())
                .filter(
                  (p, index, self) =>
                    /^\d{1,6}$/.test(p) && self.indexOf(p) === index
                );
              setPincodes(validPincodes);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pincodes"
                placeholder="Enter up to 6-digit pincodes"
                helperText="Only numeric values allowed (max 6 digits each)"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveCity} variant="contained">
            {editingCity ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageCities;
